/**
 * Dialog tạo/sửa loại lịch hẹn (danh mục hoặc quick-create từ autocomplete).
 * Hủy hỏi xác nhận; nút X và Esc đóng thẳng; backdrop không đóng.
 */
import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@/shared/ui';
import { toast } from '@/shared/feedback/toast';
import { queryKeys } from '@/shared/query';
import { getApiErrorDetails } from '@/shared/utils/apiError';
import {
  appointmentTypeAPI,
  type AppointmentType,
} from '@/modules/appointment/api/appointmentType.api';
import AppointmentTypeFormFields from './AppointmentTypeFormFields';
import {
  appointmentTypeToFormValues,
  buildAppointmentTypeFormSchema,
  emptyAppointmentTypeFormValues,
  formValuesToCreatePayload,
  formValuesToUpdatePayload,
  type AppointmentTypeFormValues,
} from '../utils/appointmentTypeForm';

export interface AppointmentTypeFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** null = tạo mới (catalog). */
  editing?: AppointmentType | null;
  mode?: 'catalog' | 'quick';
  onCreated?: (type: AppointmentType) => void;
}

const AppointmentTypeFormDialog: React.FC<AppointmentTypeFormDialogProps> = ({
  open,
  onClose,
  editing = null,
  mode = 'catalog',
  onCreated,
}) => {
  const { t } = useTranslation('appointment');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();
  const isEdit = Boolean(editing);
  const isQuick = mode === 'quick';

  const schema = useMemo(() => buildAppointmentTypeFormSchema(t), [t]);
  const form = useForm<AppointmentTypeFormValues>({
    defaultValues: emptyAppointmentTypeFormValues(),
    resolver: yupResolver(schema) as unknown as Resolver<AppointmentTypeFormValues>,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { handleSubmit, reset } = form;

  useEffect(() => {
    if (!open) return;
    reset(editing ? appointmentTypeToFormValues(editing) : emptyAppointmentTypeFormValues());
  }, [open, editing, reset]);

  const invalidateTypes = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.appointment.all });

  const saveMutation = useMutation({
    mutationFn: async (values: AppointmentTypeFormValues) => {
      if (isEdit && editing) {
        return appointmentTypeAPI.update(editing.id, formValuesToUpdatePayload(values));
      }
      return appointmentTypeAPI.create(formValuesToCreatePayload(values));
    },
    onSuccess: async (response) => {
      const saved = response.data;
      toast.success(isEdit ? t('catalog.toasts.updated') : t('catalog.toasts.created'));
      await invalidateTypes();
      onClose();
      if (!isEdit && saved && onCreated) {
        onCreated(saved);
      }
    },
    onError: (err) => {
      toast.error(
        getApiErrorDetails(err, t('catalog.toasts.saveFailed')).message,
      );
    },
  });

  const busy = saveMutation.isPending;
  const title = isEdit ? t('catalog.dialogs.editTitle') : t('catalog.dialogs.addTitle');

  return (
    <FormDialog
      open={open}
      title={title}
      busy={busy}
      onClose={onClose}
      maxWidth={isQuick ? 'xs' : 'sm'}
      minHeight={isQuick ? 320 : 480}
      contentSx={{ '&&': { pt: 2.5 } }}
      onSubmit={() => void handleSubmit((values) => saveMutation.mutate(values))()}
      submitLabel={tCommon('save')}
      busySubmitLabel={tCommon('saving')}
    >
      <FormProvider {...form}>
        <AppointmentTypeFormFields mode={mode} isEdit={isEdit} disabled={busy} />
      </FormProvider>
    </FormDialog>
  );
};

export default AppointmentTypeFormDialog;
